import type {LatLon} from '@app/types/LocationTypes';
import {polynomialDerivative} from '@app/utils/polynoms';
import type {BesselianElements, BesselianElementsAtTime} from '@package/solarEclipse/types/BesselianElementTypes';
import {getBesselianElementsAtTime, getEclipseDeltaT} from '@package/solarEclipse/utils/besselianElements';
import {
    DEG,
    E_SQ,
    ONE_MINUS_F,
    RISE_SET_BOUNDARY_Q_SAMPLES,
    RISE_SET_BOUNDARY_STEP_HOURS,
    RISE_SET_GAP_SUBDIVISION_DEPTH,
    RISE_SET_MAX_CHORD_DEG,
    RISE_SET_TIP_REFINEMENT_SAMPLES,
} from './constants';
import {isOnSunsetSide} from './maxEclipseHorizon';
import {calculateShadowBoundaryPoint, penumbraBoundaryFundamental} from './shadowBoundary';
import {terminatorRingPoint} from './shadowOutline';

export function calculateSunriseBoundary(elements: BesselianElements, z0: number): Array<LatLon> {
    return calculateRiseSetBoundary(elements, false, z0);
}

export function calculateSunsetBoundary(elements: BesselianElements, z0: number): Array<LatLon> {
    return calculateRiseSetBoundary(elements, true, z0);
}

export function calculateRiseSetBoundary(elements: BesselianElements, isSunset: boolean, z0: number): Array<LatLon> {
    let best: Array<LatLon> = [];
    for (const run of calculateRiseSetRuns(elements, z0)) {
        const cycle = assembleRunCycle(run);
        const sunsetCount = cycle.filter((p) => p.isSunset).length;
        const runIsSunset = sunsetCount * 2 > cycle.length;
        if (runIsSunset === isSunset && cycle.length > best.length) {
            best = cycle.map((p) => p.point);
        }
    }
    if (best.length > 0) {
        best.push({...best[0]});
    }

    return best;
}

interface SidedPoint {
    point: LatLon;
    isSunset: boolean;
}

interface RiseSetRun {
    leadingEdge: Array<SidedPoint>;
    trailingEdge: Array<SidedPoint>;
    startTip: SidedPoint | null;
    endTip: SidedPoint | null;
}

function calculateRiseSetRuns(elements: BesselianElements, z0: number): Array<RiseSetRun> {
    const deltaT = getEclipseDeltaT(elements);
    const runs: Array<RiseSetRun> = [];
    let current: RiseSetRun | null = null;

    let prevCount = 0;
    let lastSingleBranch: 'leading' | 'trailing' | null = null;

    const feed = (crossings: Array<TerminatorCrossing>): void => {
        if (current === null || crossings.length === 0) {
            return;
        }
        const {leadingEdge, trailingEdge} = current;
        const leadingLast = leadingEdge.length > 0 ? leadingEdge[leadingEdge.length - 1].point : null;
        const trailingLast = trailingEdge.length > 0 ? trailingEdge[trailingEdge.length - 1].point : null;

        if (crossings.length >= 2) {
            const c0 = {point: crossings[0].point, isSunset: crossings[0].isSunset};
            const c1 = {point: crossings[1].point, isSunset: crossings[1].isSunset};
            if (leadingLast === null && trailingLast === null) {
                if (crossings[0].qCos >= crossings[1].qCos) {
                    leadingEdge.push(c0);
                    trailingEdge.push(c1);
                } else {
                    leadingEdge.push(c1);
                    trailingEdge.push(c0);
                }
            } else if (leadingLast !== null && trailingLast !== null) {
                const dLL0 = latLonDistSq(c0.point, leadingLast);
                const dLL1 = latLonDistSq(c1.point, leadingLast);
                const dTL0 = latLonDistSq(c0.point, trailingLast);
                const dTL1 = latLonDistSq(c1.point, trailingLast);
                if (dLL0 + dTL1 <= dLL1 + dTL0) {
                    leadingEdge.push(c0);
                    trailingEdge.push(c1);
                } else {
                    leadingEdge.push(c1);
                    trailingEdge.push(c0);
                }
            } else if (leadingLast !== null) {
                // Trailing branch hasn't started yet — continue leading with the closer
                // crossing, fork the other into a fresh trailing branch.
                if (latLonDistSq(c0.point, leadingLast) <= latLonDistSq(c1.point, leadingLast)) {
                    leadingEdge.push(c0);
                    trailingEdge.push(c1);
                } else {
                    leadingEdge.push(c1);
                    trailingEdge.push(c0);
                }
            } else {
                // Mirror of the previous case.
                if (latLonDistSq(c0.point, trailingLast as LatLon) <= latLonDistSq(c1.point, trailingLast as LatLon)) {
                    trailingEdge.push(c0);
                    leadingEdge.push(c1);
                } else {
                    trailingEdge.push(c1);
                    leadingEdge.push(c0);
                }
            }
            lastSingleBranch = null;
        } else {
            const c = crossings[0];
            let target: 'leading' | 'trailing';
            if (lastSingleBranch !== null) {
                target = lastSingleBranch;
            } else if (leadingLast !== null && trailingLast !== null) {
                target =
                    latLonDistSq(c.point, leadingLast) <= latLonDistSq(c.point, trailingLast) ? 'leading' : 'trailing';
            } else if (leadingLast !== null) {
                target = 'leading';
            } else if (trailingLast !== null) {
                target = 'trailing';
            } else {
                target = c.qCos >= 0 ? 'leading' : 'trailing';
            }
            if (target === 'leading') {
                leadingEdge.push({point: c.point, isSunset: c.isSunset});
            } else {
                trailingEdge.push({point: c.point, isSunset: c.isSunset});
            }
            lastSingleBranch = target;
        }
    };

    const feedTipNeighborhood = (tangentTau: number, gridTau: number): void => {
        const span = gridTau - tangentTau;
        const N = RISE_SET_TIP_REFINEMENT_SAMPLES;
        for (let i = 1; i < N; i++) {
            const k = span > 0 ? i : N - i;
            feed(crossingsAtTau(elements, tangentTau + span * (k / N) ** 2, z0, deltaT));
        }
    };

    const needsSubdivision = (crossings: Array<TerminatorCrossing>): boolean => {
        if (current === null) {
            return false;
        }
        const ends: Array<LatLon> = [];
        if (current.leadingEdge.length > 0) {
            ends.push(current.leadingEdge[current.leadingEdge.length - 1].point);
        }
        if (current.trailingEdge.length > 0) {
            ends.push(current.trailingEdge[current.trailingEdge.length - 1].point);
        }
        if (ends.length === 0) {
            return false;
        }

        return crossings.some((c) =>
            ends.every((end) => {
                let dLon = Math.abs(c.point.lon - end.lon);
                if (dLon > 180) {
                    dLon = 360 - dLon;
                }
                dLon *= Math.cos(((c.point.lat + end.lat) / 2) * DEG);
                const dLat = c.point.lat - end.lat;

                return dLat * dLat + dLon * dLon > RISE_SET_MAX_CHORD_DEG ** 2;
            }),
        );
    };

    const feedRefined = (tauA: number, tauB: number, crossingsB: Array<TerminatorCrossing>, depth: number): void => {
        if (depth > 0 && needsSubdivision(crossingsB)) {
            const mid = (tauA + tauB) / 2;
            feedRefined(tauA, mid, crossingsAtTau(elements, mid, z0, deltaT), depth - 1);
            feedRefined(mid, tauB, crossingsB, depth - 1);

            return;
        }
        feed(crossingsB);
    };

    for (let tau = elements.tMin; tau <= elements.tMax; tau += RISE_SET_BOUNDARY_STEP_HOURS) {
        const crossings = crossingsAtTau(elements, tau, z0, deltaT);

        if (crossings.length === 0) {
            if (current !== null) {
                // Close the run; a 2 → 0 transition is a true tangent, 1 → 0 is not.
                if (prevCount >= 2) {
                    const tangent = bisectEndTangent(elements, tau - RISE_SET_BOUNDARY_STEP_HOURS, tau, z0, deltaT);
                    if (tangent !== null) {
                        feedTipNeighborhood(tangent.tau, tau - RISE_SET_BOUNDARY_STEP_HOURS);
                        current.endTip = tangent.tip;
                    }
                }
                runs.push(current);
                current = null;
            }
            lastSingleBranch = null;
            prevCount = 0;
            continue;
        }

        if (current === null) {
            current = {leadingEdge: [], trailingEdge: [], startTip: null, endTip: null};
            lastSingleBranch = null;
            // A 0 → 2 transition is a true tangent; skip when the sweep starts mid-run at tMin.
            if (crossings.length >= 2 && tau > elements.tMin) {
                const tangent = bisectEndTangent(elements, tau, tau - RISE_SET_BOUNDARY_STEP_HOURS, z0, deltaT);
                if (tangent !== null) {
                    current.startTip = tangent.tip;
                    feedTipNeighborhood(tangent.tau, tau);
                }
            }
        }

        feedRefined(tau - RISE_SET_BOUNDARY_STEP_HOURS, tau, crossings, RISE_SET_GAP_SUBDIVISION_DEPTH);
        prevCount = crossings.length;
    }
    if (current !== null) {
        runs.push(current);
    }

    return runs;
}

function assembleRunCycle(run: RiseSetRun): Array<SidedPoint> {
    const cycle: Array<SidedPoint> = [];
    if (run.startTip !== null) {
        cycle.push(run.startTip);
    }
    cycle.push(...run.leadingEdge);
    if (run.endTip !== null) {
        cycle.push(run.endTip);
    }
    for (let i = run.trailingEdge.length - 1; i >= 0; i--) {
        cycle.push(run.trailingEdge[i]);
    }

    return cycle;
}

interface TerminatorCrossing {
    point: LatLon;
    qCos: number;
    isSunset: boolean;
}

function bisectEndTangent(
    elements: BesselianElements,
    tauWithTwo: number,
    tauWithLess: number,
    z0: number,
    deltaT: number,
): {tip: SidedPoint; tau: number} | null {
    let twoSide = tauWithTwo;
    let lessSide = tauWithLess;
    for (let iter = 0; iter < 40; iter++) {
        const mid = (twoSide + lessSide) / 2;
        const c = crossingsAtTau(elements, mid, z0, deltaT);
        if (c.length >= 2) {
            twoSide = mid;
        } else {
            lessSide = mid;
        }
        if (Math.abs(lessSide - twoSide) < 1e-8) {
            break;
        }
    }
    const c = crossingsAtTau(elements, twoSide, z0, deltaT);
    if (c.length === 0) {
        return null;
    }
    if (c.length === 1) {
        return {tip: {point: c[0].point, isSunset: c[0].isSunset}, tau: twoSide};
    }

    const e = getBesselianElementsAtTime(elements, twoSide);
    const tangencyPoint = refineTangentToHorizon(elements, e, z0);
    if (tangencyPoint !== null) {
        return {tip: {point: tangencyPoint, isSunset: c[0].isSunset}, tau: twoSide};
    }

    const dlon = ((c[1].point.lon - c[0].point.lon + 540) % 360) - 180;
    let avgLon = c[0].point.lon + dlon / 2;
    while (avgLon > 180) {
        avgLon -= 360;
    }
    while (avgLon < -180) {
        avgLon += 360;
    }

    return {
        tip: {point: {lat: (c[0].point.lat + c[1].point.lat) / 2, lon: avgLon}, isSunset: c[0].isSunset},
        tau: twoSide,
    };
}

function crossingsAtTau(
    elements: BesselianElements,
    tau: number,
    z0: number,
    deltaT: number,
): Array<TerminatorCrossing> {
    const e = getBesselianElementsAtTime(elements, tau);
    const dx = polynomialDerivative(elements.x, tau);
    const dy = polynomialDerivative(elements.y, tau);
    const qVelocity = Math.atan2(dy, dx);

    return collectCrossings(elements, e, qVelocity, z0, deltaT);
}

function collectCrossings(
    elements: BesselianElements,
    e: BesselianElementsAtTime,
    qVelocity: number,
    z0: number,
    deltaT: number,
): Array<TerminatorCrossing> {
    const N = RISE_SET_BOUNDARY_Q_SAMPLES;
    const pts: Array<LatLon | null> = new Array(N);
    for (let i = 0; i < N; i++) {
        pts[i] = calculateShadowBoundaryPoint(elements, e, (i / N) * 2 * Math.PI, false);
    }

    const result: Array<TerminatorCrossing> = [];
    for (let i = 0; i < N; i++) {
        const j = (i + 1) % N;
        const p1 = pts[i];
        const p2 = pts[j];
        if ((p1 === null) === (p2 === null)) {
            continue;
        }

        const q1 = (i / N) * 2 * Math.PI;
        const q2 = ((i + 1) / N) * 2 * Math.PI;
        const crossing = p1 !== null ? findNullBoundary(elements, e, q1, q2) : findNullBoundary(elements, e, q2, q1);
        if (crossing === null) {
            continue;
        }

        const point = refineCrossingToHorizon(elements, e, crossing.xi, crossing.eta, z0) ?? crossing.geometric;
        const qCrossing = (q1 + q2) / 2;
        result.push({
            point,
            qCos: Math.cos(qCrossing - qVelocity),
            isSunset: isOnSunsetSide(crossing.geometric, e, deltaT),
        });
    }

    return result;
}

function findNullBoundary(
    elements: BesselianElements,
    e: BesselianElementsAtTime,
    qNonNull: number,
    qNull: number,
): {geometric: LatLon; xi: number; eta: number} | null {
    let nonNullSide = qNonNull;
    let nullSide = qNull;

    for (let iter = 0; iter < 50; iter++) {
        const qMid = (nonNullSide + nullSide) / 2;
        const p = calculateShadowBoundaryPoint(elements, e, qMid, false);
        if (p !== null) {
            nonNullSide = qMid;
        } else {
            nullSide = qMid;
        }
        if (Math.abs(nullSide - nonNullSide) < 1e-9) {
            break;
        }
    }

    const geometric = calculateShadowBoundaryPoint(elements, e, nonNullSide, false);
    const fundamental = penumbraBoundaryFundamental(elements, e, nonNullSide);
    if (geometric === null || fundamental === null) {
        return null;
    }

    return {geometric, xi: fundamental.xi, eta: fundamental.eta};
}

function refineCrossingToHorizon(
    elements: BesselianElements,
    e: BesselianElementsAtTime,
    xiSeed: number,
    etaSeed: number,
    z0: number,
): LatLon | null {
    const penumbraRadius = Math.abs(e.l1 - z0 * elements.tanF1);
    const axisDistance = Math.hypot(e.x, e.y);
    if (axisDistance === 0) {
        return null;
    }
    const ux = e.x / axisDistance;
    const uy = e.y / axisDistance;

    let xi = xiSeed;
    let eta = etaSeed;
    // On the ring sinU follows linearly from eta: (1 - f) sinU = eta cos d + zeta sin d.
    let sinU = (etaSeed * e.cosD + z0 * e.sinD) / ONE_MINUS_F;
    for (let iter = 0; iter < 40; iter++) {
        // Radius (about the axis) of the terminator ring at zeta = z0 on the ellipsoid.
        const ringRadiusSq = 1 - E_SQ * sinU * sinU - z0 * z0;
        if (ringRadiusSq < 0) {
            return null;
        }
        const ringRadius = Math.sqrt(ringRadiusSq);
        const along =
            (ringRadius * ringRadius - penumbraRadius * penumbraRadius + axisDistance * axisDistance)
            / (2 * axisDistance);
        const halfChordSq = ringRadius * ringRadius - along * along;
        if (halfChordSq < 0) {
            return null;
        }
        const halfChord = Math.sqrt(halfChordSq);
        const baseXi = along * ux;
        const baseEta = along * uy;
        const candidate1Xi = baseXi - halfChord * uy;
        const candidate1Eta = baseEta + halfChord * ux;
        const candidate2Xi = baseXi + halfChord * uy;
        const candidate2Eta = baseEta - halfChord * ux;
        const dist1 = (candidate1Xi - xi) ** 2 + (candidate1Eta - eta) ** 2;
        const dist2 = (candidate2Xi - xi) ** 2 + (candidate2Eta - eta) ** 2;
        const nextXi = dist1 <= dist2 ? candidate1Xi : candidate2Xi;
        const nextEta = dist1 <= dist2 ? candidate1Eta : candidate2Eta;
        const moved = (nextXi - xi) ** 2 + (nextEta - eta) ** 2;
        xi = nextXi;
        eta = nextEta;
        sinU = (eta * e.cosD + z0 * e.sinD) / ONE_MINUS_F;
        if (moved < 1e-18) {
            break;
        }
    }

    return terminatorRingPoint(elements, e, Math.atan2(eta, xi), sinU, z0).point;
}

function refineTangentToHorizon(elements: BesselianElements, e: BesselianElementsAtTime, z0: number): LatLon | null {
    const penumbraRadius = Math.abs(e.l1 - z0 * elements.tanF1);
    const axisDistance = Math.hypot(e.x, e.y);
    if (axisDistance === 0) {
        return null;
    }
    const ux = e.x / axisDistance;
    const uy = e.y / axisDistance;

    let xi = ux;
    let eta = uy;
    let sinU = (uy * e.cosD + z0 * e.sinD) / ONE_MINUS_F;
    for (let iter = 0; iter < 40; iter++) {
        const ringRadiusSq = 1 - E_SQ * sinU * sinU - z0 * z0;
        if (ringRadiusSq < 0) {
            return null;
        }
        const along =
            (ringRadiusSq - penumbraRadius * penumbraRadius + axisDistance * axisDistance) / (2 * axisDistance);
        xi = along * ux;
        eta = along * uy;
        const next = (eta * e.cosD + z0 * e.sinD) / ONE_MINUS_F;
        const moved = (next - sinU) ** 2;
        sinU = next;
        if (moved < 1e-18) {
            break;
        }
    }

    return terminatorRingPoint(elements, e, Math.atan2(eta, xi), sinU, z0).point;
}

function latLonDistSq(a: LatLon, b: LatLon): number {
    let dLon = b.lon - a.lon;
    while (dLon > 180) {
        dLon -= 360;
    }
    while (dLon < -180) {
        dLon += 360;
    }
    const dLat = b.lat - a.lat;

    return dLat * dLat + dLon * dLon;
}
